import { format, isToday, parse, parseISO } from "date-fns"

/**
 * Formats a timestamp for display in lists.
 * Shows time (e.g., "2:30 PM") if today, otherwise shows date (e.g., "1/15/26").
 * @param {string} timestamp - ISO 8601 timestamp string
 * @returns {string} Formatted timestamp
 */
export const formatListTimestamp = (timestamp) => {
  if (!timestamp) return ""
  const date = parseISO(timestamp)
  if (isToday(date)) {
    return format(date, "h:mm a")
  }
  return format(date, "M/d/yy")
}

/**
 * Formats a 24-hour time string to 12-hour format.
 * @param {string} time - Time string in "HH:mm" format (e.g., "14:30")
 * @returns {string} Formatted time (e.g., "2:30 pm")
 */
export const formatTime24to12 = (time) => {
  if (!time) return ""
  try {
    return format(parse(time, "HH:mm", new Date()), "h:mm a").toLowerCase()
  } catch {
    return ""
  }
}

/**
 * Formats a time range from 24-hour to 12-hour format.
 * @param {string[]} timeRange - Array of two time strings in "HH:mm" format (e.g., ["08:00", "17:00"])
 * @returns {string} Formatted time range (e.g., "8:00 am - 5:00 pm")
 */
export const formatTimeRange = (timeRange) => {
  if (!timeRange || timeRange.length !== 2) return ""
  return `${formatTime24to12(timeRange[0])} - ${formatTime24to12(timeRange[1])}`
}

/**
 * Formats a date string as MM/DD.
 * @param {string} dateStr - Date string (ISO format YYYY-MM-DD or MM/DD/YYYY)
 * @returns {string} Formatted date (e.g., "01/30")
 */
export const formatShortDate = (dateStr) => {
  if (!dateStr) return ""
  // Handle both YYYY-MM-DD and MM/DD/YYYY formats
  let date
  if (dateStr.includes("-")) {
    date = parseISO(dateStr)
  } else {
    date = parse(dateStr, "MM/dd/yyyy", new Date())
  }
  return format(date, "MM/dd")
}

/**
 * Formats a date range for display.
 * Shows single date if start equals end, otherwise shows range with en-dash.
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @returns {string} Formatted range (e.g., "01/30" or "01/30 – 02/02")
 */
export const formatDateRange = (startDate, endDate) => {
  const start = formatShortDate(startDate)
  const end = formatShortDate(endDate)
  if (!start) return ""
  if (start === end || !end) return start
  return `${start} – ${end}` // en-dash for range
}
